import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { ChevronDown, ChevronRight, RotateCcw, Lock } from 'lucide-react';

/**
 * WorkflowGatingSection — Feb 2026
 *
 * Renders the "Workflow Gating" accordion on the Competition setup
 * page. Two rows: Figure Draw timing + Order of Skate Draw timing.
 * Each row shows the current value + a "Reset to Preset Default"
 * button when the value differs from the preset for the current
 * competition type. The backend seeds ``workflow_gating`` from the
 * type preset on create + when the type changes (see
 * ``backend/utils/workflow_gating.py``); this UI is the manual
 * override.
 *
 * Auto-expands the FIRST time it is mounted after a competition-type
 * change so the user can review what the type preset assigned.
 */

const FIGURE_DRAW_OPTIONS = [
  {
    value: 'no_draw_required',
    label: 'No figure draw required (set figures per grade)',
    hint: 'Use when the schedule locks in the figures for every grade — e.g. Auckland Open Levels 1-5.',
  },
  {
    value: 'with_event_notice',
    label: 'Drawn before Event Notice issued',
    hint: 'Group + foot drawn ahead of the Event Notice; figures appear inside the notice.',
  },
  {
    value: 'standard_pre_programme',
    label: 'Standard Draw (group ~3 wks / foot ~1 wk before)',
    hint: 'Standard Draw completed between the Event Notice and the Programme printing.',
  },
  {
    value: 'post_programme',
    label: 'Figure draw announced at manager\u2019s meeting',
    hint: 'International-style — the Programme lists competitors and the draw is announced at the event.',
  },
];

const OOS_DRAW_OPTIONS = [
  {
    value: 'pre_programme',
    label: 'Drawn before the Programme is printed',
    hint: 'Automated for Local/Regional, may be manual for larger competitions.',
  },
  {
    value: 'post_programme',
    label: 'Draw after the Programme is printed',
    hint: 'International-style — the Programme lists competitors alphabetically with blank order lines.',
  },
];

// Type-preset defaults must mirror ``backend/utils/workflow_gating.PRESETS``.
const PRESET_BY_TYPE = {
  Local:          { figure_draw_timing: 'with_event_notice',      oos_draw_timing: 'pre_programme' },
  Regional:       { figure_draw_timing: 'with_event_notice',      oos_draw_timing: 'pre_programme' },
  'Area/State':   { figure_draw_timing: 'standard_pre_programme', oos_draw_timing: 'pre_programme' },
  National:       { figure_draw_timing: 'standard_pre_programme', oos_draw_timing: 'pre_programme' },
  International:  { figure_draw_timing: 'post_programme',         oos_draw_timing: 'post_programme' },
  World:          { figure_draw_timing: 'post_programme',         oos_draw_timing: 'post_programme' },
  Invitational:   { figure_draw_timing: 'post_programme',         oos_draw_timing: 'post_programme' },
};

const presetFor = (type) => PRESET_BY_TYPE[type] || PRESET_BY_TYPE.Local;

const _formatLockWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return ''; }
};

const LockPadlock = ({ lock, testid }) => {
  if (!lock?.locked_at) return null;
  const tooltip =
    `Locked on ${_formatLockWhen(lock.locked_at)} by ${lock.locked_by_name || 'unknown'}`
    + ` — the draw has been distributed and cannot be changed without unlocking.`;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-stone-500"
      title={tooltip}
      data-testid={testid}
    >
      <Lock className="h-3.5 w-3.5" />
    </span>
  );
};

export function WorkflowGatingSection({ data, setData, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const preset = presetFor(data.competition_type);
  const gating = data.workflow_gating || preset;

  // Immutability locks (see backend/utils/workflow_locking.py).
  // When a draw type has been distributed, the corresponding row is
  // locked here too so the user can't quietly swap the timing setting
  // out from under a draw that's already gone out.
  const locks = data.workflow_locks || {};
  const figureLock = locks.figure_draw || null;
  const oosLock = locks.oos_draw || null;

  const set = (key, value) => {
    if (readOnly) return;
    setData({
      ...data,
      workflow_gating: { ...gating, [key]: value },
    });
  };

  const resetRow = (key) => set(key, preset[key]);

  const isDefault = (key) => gating[key] === preset[key];

  return (
    <Card className="p-6" data-testid="workflow-gating-section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid="workflow-gating-toggle"
      >
        <div>
          <h3 className="text-lg font-semibold text-stone-800">Workflow Gating</h3>
          <p className="text-xs text-stone-500 mt-1">
            Which upstream steps must be complete before Event Notice,
            Programme, and Officials documents can be generated.
            Defaults come from the competition type; edit any row to
            override.
          </p>
        </div>
        {open ? (
          <ChevronDown className="h-5 w-5 text-stone-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-5 w-5 text-stone-400 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="mt-5 space-y-5" data-testid="workflow-gating-panel">
          {/* Figure Draw timing */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="font-medium flex items-center gap-2">
                Figure Draw timing
                <LockPadlock lock={figureLock} testid="workflow-gating-figure-draw-lock-icon" />
              </Label>
              {!isDefault('figure_draw_timing') && !figureLock && (
                <button
                  type="button"
                  onClick={() => resetRow('figure_draw_timing')}
                  className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
                  data-testid="workflow-gating-reset-figure-draw"
                  disabled={readOnly}
                >
                  <RotateCcw className="h-3 w-3" /> Reset to preset default
                </button>
              )}
            </div>
            <select
              value={gating.figure_draw_timing || preset.figure_draw_timing}
              onChange={(e) => set('figure_draw_timing', e.target.value)}
              disabled={readOnly || !!figureLock}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${figureLock ? 'text-stone-500 cursor-not-allowed' : ''}`}
              title={figureLock ? `Locked on ${_formatLockWhen(figureLock.locked_at)} by ${figureLock.locked_by_name || 'unknown'}` : undefined}
              data-testid="workflow-gating-figure-draw-select"
            >
              {FIGURE_DRAW_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs italic text-stone-500 mt-1.5">
              {FIGURE_DRAW_OPTIONS.find(
                (o) => o.value === (gating.figure_draw_timing || preset.figure_draw_timing)
              )?.hint}
            </p>
          </div>

          {/* OOS Draw timing */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="font-medium flex items-center gap-2">
                Order of Skate draw timing
                <LockPadlock lock={oosLock} testid="workflow-gating-oos-draw-lock-icon" />
              </Label>
              {!isDefault('oos_draw_timing') && !oosLock && (
                <button
                  type="button"
                  onClick={() => resetRow('oos_draw_timing')}
                  className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
                  data-testid="workflow-gating-reset-oos-draw"
                  disabled={readOnly}
                >
                  <RotateCcw className="h-3 w-3" /> Reset to preset default
                </button>
              )}
            </div>
            <select
              value={gating.oos_draw_timing || preset.oos_draw_timing}
              onChange={(e) => set('oos_draw_timing', e.target.value)}
              disabled={readOnly || !!oosLock}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${oosLock ? 'text-stone-500 cursor-not-allowed' : ''}`}
              title={oosLock ? `Locked on ${_formatLockWhen(oosLock.locked_at)} by ${oosLock.locked_by_name || 'unknown'}` : undefined}
              data-testid="workflow-gating-oos-draw-select"
            >
              {OOS_DRAW_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs italic text-stone-500 mt-1.5">
              {OOS_DRAW_OPTIONS.find(
                (o) => o.value === (gating.oos_draw_timing || preset.oos_draw_timing)
              )?.hint}
            </p>
          </div>

          {/* Fixed-requirement info panel */}
          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-xs text-stone-600">
            <span className="font-semibold text-stone-700">Always required</span>
            {' '}before Programme generation: entries have closed, grade
            grouping is locked, and the Event Notice has been issued.
            Officials Documents (Run Sheet, Judges Sheets) require
            everything above <em>plus</em> the draws configured above.
          </div>
        </div>
      )}
    </Card>
  );
}
